import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Link } from 'react-router';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { useMe } from '@/auth/queries';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AVATARES } from '@/lib/avatares';
import { cn, tratamiento } from '@/lib/utils';

import { Cargando, ErrorCarga } from '../curso/componentes';

type Historia = components['schemas']['EnarmScoresHistory'];
type Fila = Historia['rows'][number];
type Medida = 'min' | 'max';
type Columna = 'year' | 'specialty' | 'max' | 'min';
type Orden = { col: Columna; dir: 'asc' | 'desc' };
/** Especialidad elegida y su color: el color sigue a la especialidad, no a su posición. */
type Serie = { nombre: string; slot: number };

/** Las 8 series de `--serie-1…8` (orden validado para daltonismo). */
const MAX_SERIES = 8;
/** Si el alumno no tiene especialidad, se arranca con las cuatro troncales. */
const TRONCALES = ['Cirugía General', 'Medicina Interna', 'Pediatría', 'Ginecología y Obstetricia'];
const MEDIDAS: Record<Medida, string> = { min: 'Mínimo', max: 'Máximo' };

const n = (v: number) => v.toFixed(4).replace(/\.?0+$/, '');
const color = (slot: number) => `var(--serie-${String(slot + 1)})`;
const mismo = (a: string, b: string) => a.localeCompare(b, 'es', { sensitivity: 'base' }) === 0;

/** Puntajes ENARM por especialidad (fuente: CIFRHS): gráfica histórica + tabla filtrable. */
export function PuntajesPage() {
  const { data: me } = useMe();
  const { data, error, isPending } = useQuery({
    queryKey: ['puntajes', 'historia'],
    queryFn: () => unwrap<Historia>(api.GET('/api/v1/catalogs/scores/history')),
  });
  if (isPending) return <Cargando />;
  if (error) return <ErrorCarga error={error} />;
  if (data.rows.length === 0) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Titulo />
        <p className="panel-vidrio text-center text-texto-2">Aún no hay puntajes cargados.</p>
      </div>
    );
  }
  return (
    <Puntajes
      data={data}
      saludo={me ? tratamiento(me.nickname || me.name, me.gender) : ''}
      avatar={me?.gender === 'H' ? AVATARES.doctorSenala : AVATARES.doctoraSenala}
    />
  );
}

function Titulo({ rango }: { rango?: string }) {
  return (
    <div className="space-y-1">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Puntajes ENARM</span>
      </h1>
      <p className="text-sm text-texto-2">
        Puntajes máximo y mínimo de los médicos mexicanos seleccionados en cada especialidad
        {rango ? ` (${rango})` : ''}. Fuente: CIFRHS.
      </p>
    </div>
  );
}

function Puntajes({ data, saludo, avatar }: { data: Historia; saludo: string; avatar: string }) {
  const anios = useMemo(() => [...data.years].sort((a, b) => a - b), [data.years]);
  const primero = anios[0] ?? 0;
  const ultimo = anios[anios.length - 1] ?? 0;
  const especialidades = useMemo(
    () => [...new Set(data.rows.map((r) => r.specialty))].sort((a, b) => a.localeCompare(b, 'es')),
    [data.rows],
  );
  const mia = data.rows.find((r) => r.mine && r.year === ultimo);
  const nombreMio = data.rows.find((r) => r.mine)?.specialty;

  const [desde, setDesde] = useState(primero);
  const [hasta, setHasta] = useState(ultimo);
  const [medida, setMedida] = useState<Medida>('min');
  const [orden, setOrden] = useState<Orden>({ col: 'year', dir: 'desc' });
  const [series, setSeries] = useState<Serie[]>(() => {
    const iniciales = nombreMio
      ? [nombreMio, ...TRONCALES.filter((t) => !mismo(t, nombreMio)).slice(0, 3)]
      : TRONCALES;
    return iniciales
      .filter((nombre) => especialidades.includes(nombre))
      .map((nombre, slot) => ({ nombre, slot }));
  });

  const alternar = (nombre: string) =>
    setSeries((prev) => {
      if (prev.some((s) => s.nombre === nombre)) return prev.filter((s) => s.nombre !== nombre);
      if (prev.length >= MAX_SERIES) return prev;
      const usados = new Set(prev.map((s) => s.slot));
      const slot = [...Array(MAX_SERIES).keys()].find((i) => !usados.has(i)) ?? 0;
      return [...prev, { nombre, slot }];
    });

  const visibles = anios.filter((y) => y >= desde && y <= hasta);
  const elegidas = new Set(series.map((s) => s.nombre));
  const filas = data.rows.filter(
    (r) => r.year >= desde && r.year <= hasta && (elegidas.size === 0 || elegidas.has(r.specialty)),
  );
  const slotDe = new Map(series.map((s) => [s.nombre, s.slot]));

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Titulo rango={`${String(primero)}–${String(ultimo)}`} />

      {mia ? (
        // La mascota (a la derecha) señala tu meta, como los avatares del legacy.
        <p className="panel-vidrio flex items-center gap-4 border-l-4 border-l-cian-500 py-4">
          <span className="flex-1">
            {saludo}, para <strong>{mia.specialty}</strong> debes alcanzar al menos{' '}
            <strong className="text-cian-texto">{n(mia.min + 3)} puntos</strong> (3 por encima del
            mínimo de {ultimo}). ¡Sigue preparándote!
          </span>
          <img src={avatar} alt="" className="pointer-events-none h-20 w-auto shrink-0" />
        </p>
      ) : (
        <p className="text-sm text-texto-2">
          Elige tu especialidad deseada en{' '}
          <Link to="/cuenta" className="font-bold text-azul-texto underline">
            Mi cuenta
          </Link>{' '}
          para ver tu meta.
        </p>
      )}

      {/* Filtros: una sola fila, aplican a la gráfica y a la tabla */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-xs font-bold text-texto-3">
          Desde
          <select
            className="input-vidrio w-28"
            value={desde}
            onChange={(e) => {
              const v = Number(e.target.value);
              setDesde(v);
              if (v > hasta) setHasta(v);
            }}
          >
            {anios.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-xs font-bold text-texto-3">
          Hasta
          <select
            className="input-vidrio w-28"
            value={hasta}
            onChange={(e) => {
              const v = Number(e.target.value);
              setHasta(v);
              if (v < desde) setDesde(v);
            }}
          >
            {anios.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <div className="grid gap-1 text-xs font-bold text-texto-3">
          Especialidades
          <DropdownMenu>
            <DropdownMenuTrigger className="input-vidrio flex w-64 items-center justify-between gap-2 text-left text-sm">
              <span className="truncate">
                {series.length === 0
                  ? 'Todas (elige para graficar)'
                  : `${String(series.length)} seleccionada${series.length === 1 ? '' : 's'}`}
              </span>
              <ChevronDown className="size-4 shrink-0 text-texto-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-[60vh] w-72 overflow-y-auto">
              <DropdownMenuLabel>
                Hasta {MAX_SERIES} en la gráfica ({series.length}/{MAX_SERIES})
              </DropdownMenuLabel>
              {especialidades.map((e) => {
                const marcada = elegidas.has(e);
                return (
                  <DropdownMenuCheckboxItem
                    key={e}
                    checked={marcada}
                    disabled={!marcada && series.length >= MAX_SERIES}
                    onSelect={(ev) => {
                      ev.preventDefault();
                      alternar(e);
                    }}
                  >
                    <span className="flex-1">{e}</span>
                    {nombreMio && mismo(e, nombreMio) && (
                      <span className="insignia insignia-activa">Tuya</span>
                    )}
                  </DropdownMenuCheckboxItem>
                );
              })}
              {series.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setSeries([])}>Quitar todas</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="grid gap-1 text-xs font-bold text-texto-3">
          Graficar
          <div className="flex gap-2" role="group" aria-label="Puntaje a graficar">
            {(Object.keys(MEDIDAS) as Medida[]).map((m) => (
              <button
                key={m}
                type="button"
                className="btn-modulo py-2.5"
                data-state={medida === m ? 'active' : undefined}
                aria-pressed={medida === m}
                onClick={() => setMedida(m)}
              >
                {MEDIDAS[m]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {series.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Especialidades seleccionadas">
          {series.map((s) => (
            <li key={s.nombre}>
              <button
                type="button"
                onClick={() => alternar(s.nombre)}
                className="vidrio flex items-center gap-2 rounded-full py-1 pr-2 pl-3 text-xs font-bold text-texto hover:bg-[var(--vidrio-fuerte)]"
                aria-label={`Quitar ${s.nombre}`}
              >
                <span className="size-2.5 rounded-full" style={{ background: color(s.slot) }} />
                {s.nombre}
                <X className="size-3.5 text-texto-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <section className="panel-vidrio space-y-3 p-4 sm:p-6">
        <h2 className="font-bold text-texto">Puntaje {MEDIDAS[medida].toLowerCase()} por año</h2>
        {series.length === 0 ? (
          <p className="py-10 text-center text-sm text-texto-2">
            Elige una o más especialidades para ver su evolución.
          </p>
        ) : visibles.length < 2 ? (
          <p className="py-10 text-center text-sm text-texto-2">
            Elige un rango de al menos dos años para ver la tendencia.
          </p>
        ) : (
          <Grafica
            anios={visibles}
            medida={medida}
            series={series.map((s) => ({
              ...s,
              mia: !!nombreMio && mismo(s.nombre, nombreMio),
              valores: new Map(
                data.rows.filter((r) => r.specialty === s.nombre).map((r) => [r.year, r[medida]]),
              ),
            }))}
          />
        )}
      </section>

      <Tabla filas={filas} orden={orden} setOrden={setOrden} slotDe={slotDe} />
    </div>
  );
}

/* ------------------------------------------------------------------ gráfica */

type SerieGrafica = Serie & { mia: boolean; valores: Map<number, number> };

const ALTO = 300;
const M = { top: 16, bottom: 32, left: 40 };

/** Líneas con puntos por especialidad, eje Y único, cruz con tooltip al pasar el cursor. */
function Grafica({
  anios,
  series,
  medida,
}: {
  anios: number[];
  series: SerieGrafica[];
  medida: Medida;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(640);
  const [activo, setActivo] = useState<number | null>(null);

  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      if (e) setAncho(Math.round(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const etiquetasDirectas = series.length <= 4 && ancho >= 560;
  const derecha = etiquetasDirectas ? 150 : 16;
  const valores = series.flatMap((s) => anios.flatMap((y) => s.valores.get(y) ?? []));
  const yMin = Math.max(0, Math.floor((Math.min(...valores) - 2) / 5) * 5);
  const yMax = Math.min(100, Math.ceil((Math.max(...valores) + 2) / 5) * 5);
  const paso = yMax - yMin > 40 ? 10 : 5;
  const ticks: number[] = [];
  for (let v = yMin; v <= yMax; v += paso) ticks.push(v);

  const w = ancho - M.left - derecha;
  const h = ALTO - M.top - M.bottom;
  const x = (i: number) => M.left + (anios.length === 1 ? w / 2 : (i * w) / (anios.length - 1));
  const y = (v: number) => M.top + h - ((v - yMin) / (yMax - yMin)) * h;

  /** Tramos continuos: un año sin dato corta la línea (p. ej. Medicina Legal o Paliativa). */
  const tramos = (s: SerieGrafica) => {
    const out: string[] = [];
    let actual: string[] = [];
    anios.forEach((a, i) => {
      const v = s.valores.get(a);
      if (v === undefined) {
        if (actual.length > 1) out.push(actual.join(' '));
        actual = [];
      } else actual.push(`${actual.length ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    });
    if (actual.length > 1) out.push(actual.join(' '));
    return out;
  };

  // Etiquetas al final de cada línea, separadas para que no se encimen.
  const finales = series
    .map((s) => {
      const i = anios.findLastIndex((a) => s.valores.has(a));
      const v = i >= 0 ? s.valores.get(anios[i] ?? 0) : undefined;
      return v === undefined ? null : { s, i, v, ty: y(v) };
    })
    .filter((e) => e !== null)
    .sort((a, b) => a.ty - b.ty);
  for (let k = 1; k < finales.length; k++) {
    const prev = finales[k - 1];
    const cur = finales[k];
    if (prev && cur && cur.ty - prev.ty < 15) cur.ty = prev.ty + 15;
  }

  const mover = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    const i = Math.round(((px - M.left) / Math.max(w, 1)) * (anios.length - 1));
    setActivo(Math.max(0, Math.min(anios.length - 1, i)));
  };
  const teclas = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const d = e.key === 'ArrowLeft' ? -1 : 1;
    setActivo((a) => Math.max(0, Math.min(anios.length - 1, (a ?? anios.length - 1) + d)));
  };

  const anioActivo = activo === null ? null : anios[activo];
  const enTooltip =
    anioActivo === undefined || anioActivo === null
      ? []
      : series
          .map((s) => ({ s, v: s.valores.get(anioActivo) }))
          .filter((e): e is { s: SerieGrafica; v: number } => e.v !== undefined)
          .sort((a, b) => b.v - a.v);
  const tipX = activo === null ? 0 : x(activo);

  return (
    <div ref={caja} className="relative animate__animated animate__fadeIn">
      <svg
        width={ancho}
        height={ALTO}
        className="block touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-cian-500 rounded-lg"
        role="img"
        tabIndex={0}
        aria-label={`Puntaje ${MEDIDAS[medida].toLowerCase()} de ${series.map((s) => s.nombre).join(', ')} entre ${String(anios[0])} y ${String(anios[anios.length - 1])}. Los valores exactos están en la tabla.`}
        onPointerMove={mover}
        onPointerLeave={() => setActivo(null)}
        onFocus={() => setActivo((a) => a ?? anios.length - 1)}
        onBlur={() => setActivo(null)}
        onKeyDown={teclas}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={M.left + w} y1={y(t)} y2={y(t)} stroke="var(--pista)" />
            <text
              x={M.left - 8}
              y={y(t)}
              dy="0.32em"
              textAnchor="end"
              fontSize="11"
              fill="var(--color-texto-3)"
              className="tabular"
            >
              {t}
            </text>
          </g>
        ))}
        {anios.map((a, i) => (
          <text
            key={a}
            x={x(i)}
            y={ALTO - 10}
            textAnchor="middle"
            fontSize="11"
            fontWeight={i === activo ? 800 : 400}
            fill={i === activo ? 'var(--color-texto)' : 'var(--color-texto-3)'}
            className="tabular"
          >
            {ancho < 480 && anios.length > 6 ? `’${String(a).slice(2)}` : a}
          </text>
        ))}
        {activo !== null && (
          <line
            x1={tipX}
            x2={tipX}
            y1={M.top}
            y2={M.top + h}
            stroke="var(--color-texto-3)"
            strokeDasharray="3 3"
          />
        )}
        {series.map((s) =>
          tramos(s).map((d, k) => (
            <path
              key={`${s.nombre}-${String(k)}`}
              d={d}
              fill="none"
              stroke={color(s.slot)}
              strokeWidth={s.mia ? 3 : 2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )),
        )}
        {series.map((s) =>
          anios.map((a, i) => {
            const v = s.valores.get(a);
            if (v === undefined) return null;
            const grande = i === activo;
            return (
              <circle
                key={`${s.nombre}-${String(a)}`}
                cx={x(i)}
                cy={y(v)}
                r={grande ? 5.5 : 4}
                fill={color(s.slot)}
                stroke="var(--fondo-base)"
                strokeWidth="2"
              />
            );
          }),
        )}
        {etiquetasDirectas &&
          finales.map(({ s, i, v, ty }) => (
            <text
              key={s.nombre}
              x={x(i) + 10}
              y={ty}
              dy="0.32em"
              fontSize="11"
              fontWeight={s.mia ? 800 : 600}
              fill="var(--color-texto-2)"
            >
              {s.nombre.length > 20 ? `${s.nombre.slice(0, 19)}…` : s.nombre}{' '}
              <tspan fill="var(--color-texto-3)" className="tabular">
                {n(v)}
              </tspan>
            </text>
          ))}
      </svg>

      {activo !== null && enTooltip.length > 0 && (
        <div
          className="vidrio-fuerte pointer-events-none absolute top-2 z-10 w-60 rounded-xl p-3 text-xs shadow-lg"
          style={
            tipX > ancho / 2
              ? { right: Math.max(ancho - tipX + 12, 0) }
              : { left: Math.min(tipX + 12, ancho - 240) }
          }
          role="status"
        >
          <p className="mb-1.5 font-extrabold text-texto">
            {anioActivo} · {MEDIDAS[medida]}
          </p>
          <ul className="space-y-1">
            {enTooltip.map(({ s, v }) => (
              <li key={s.nombre} className="flex items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: color(s.slot) }}
                />
                <span
                  className={cn('flex-1 truncate text-texto-2', s.mia && 'font-bold text-texto')}
                >
                  {s.nombre}
                </span>
                <span className="font-bold text-texto tabular">{n(v)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Leyenda: siempre presente (la identidad no depende solo de las etiquetas) */}
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-texto-2">
        {series.map((s) => (
          <li key={s.nombre} className="flex items-center gap-1.5">
            <svg width="18" height="10" aria-hidden>
              <line
                x1="0"
                x2="18"
                y1="5"
                y2="5"
                stroke={color(s.slot)}
                strokeWidth={s.mia ? 3 : 2}
              />
              <circle cx="9" cy="5" r="3.5" fill={color(s.slot)} />
            </svg>
            <span className={cn(s.mia && 'font-bold text-texto')}>{s.nombre}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ tabla */

const COLUMNAS: { col: Columna; label: string; num: boolean }[] = [
  { col: 'year', label: 'Año', num: false },
  { col: 'specialty', label: 'Especialidad', num: false },
  { col: 'max', label: 'Máximo', num: true },
  { col: 'min', label: 'Mínimo', num: true },
];

function comparar(a: Fila, b: Fila, { col, dir }: Orden) {
  const s = dir === 'asc' ? 1 : -1;
  const base = col === 'specialty' ? a.specialty.localeCompare(b.specialty, 'es') : a[col] - b[col];
  // Desempate estable: año más reciente primero, luego especialidad.
  return s * base || b.year - a.year || a.specialty.localeCompare(b.specialty, 'es');
}

function Tabla({
  filas,
  orden,
  setOrden,
  slotDe,
}: {
  filas: Fila[];
  orden: Orden;
  setOrden: (o: Orden) => void;
  slotDe: Map<string, number>;
}) {
  const ordenadas = [...filas].sort((a, b) => comparar(a, b, orden));
  const ordenar = (col: Columna) =>
    setOrden(
      orden.col === col
        ? { col, dir: orden.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: col === 'specialty' ? 'asc' : 'desc' },
    );

  return (
    <section className="space-y-2">
      <p className="text-xs text-texto-3">
        {ordenadas.length} registro{ordenadas.length === 1 ? '' : 's'}
        {slotDe.size === 0
          ? ' de todas las especialidades'
          : ' de las especialidades seleccionadas'}
        . Toca un encabezado para ordenar.
      </p>
      <div className="panel-vidrio max-h-[70vh] overflow-auto p-0">
        <table className="tabla-cs tabla-cs-guinda max-sm:[&_td]:px-2 max-sm:[&_th]:px-2">
          <thead className="sticky top-0 z-[1]">
            <tr>
              {COLUMNAS.map(({ col, label, num }) => {
                const activa = orden.col === col;
                const Icono = !activa ? ArrowUpDown : orden.dir === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={col}
                    aria-sort={activa ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={cn(num && 'text-right sm:w-24', col === 'year' && 'sm:w-20')}
                  >
                    <button
                      type="button"
                      onClick={() => ordenar(col)}
                      className={cn(
                        'inline-flex items-center gap-1 font-bold hover:text-azul-texto',
                        num && 'flex-row-reverse',
                      )}
                    >
                      {label}
                      <Icono
                        className={cn('size-3.5', activa ? 'text-azul-texto' : 'text-texto-3')}
                      />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((r) => {
              const slot = slotDe.get(r.specialty);
              return (
                <tr
                  key={`${String(r.year)}-${r.specialty}`}
                  {...(r.mine ? { 'data-destacada': '' } : {})}
                >
                  <td className="tabular">{r.year}</td>
                  <td className={cn(r.mine && 'font-extrabold')}>
                    <span className="inline-flex items-center gap-2">
                      {slot !== undefined && (
                        <span
                          className="size-2.5 shrink-0 rounded-full"
                          style={{ background: color(slot) }}
                          aria-hidden
                        />
                      )}
                      {r.specialty}
                      {r.mine && (
                        <span className="insignia insignia-activa max-sm:hidden">
                          Tu especialidad
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="text-right tabular">{n(r.max)}</td>
                  <td className={cn('text-right tabular', r.mine && 'font-extrabold')}>
                    {n(r.min)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
